import React from "react";
import { observer } from "mobx-react";
import { transparentize } from "polished";
import { Paper, withStyles, IconButton } from "@material-ui/core";
import ErrorIcon from "@material-ui/icons/ErrorRounded";
import PersonAddIcon from "@material-ui/icons/PersonAddRounded";
import AccountGroup from "mdi-material-ui/AccountGroup";
import { CustomerContactPersonFragment as ContactPerson } from "@graphql";
import { Body, Title, PrimaryButton, Spinner } from "@components";
import locale from "@bonliva-crm/locale";
import { Classes, ThemeType } from "@theme";
import { store } from "@store";
import CustomerContactCard from "./CustomerContactCard";

type Item = { item: ContactPerson; selected?: boolean };

export type FauxItem = {
  title: string;
  desc: string;
  onClick: () => void;
};

type Props = {
  classes: Classes<typeof styles>;
  canAdd?: boolean;
  canEdit?: boolean;
  data: Item[];
  fauxItems?: FauxItem[];
  loading?: boolean;
  highlightImportant?: boolean;
  addNewTitle?: string;
  onAdd?: (e: React.SyntheticEvent<any>) => void;
  onClick?: (item: ContactPerson) => void;
  onDelete?: (item: ContactPerson) => void;
  onEdit?: (id: string, e: React.SyntheticEvent<any>) => void;
  onSelect?: (id: string, e: React.SyntheticEvent<any>) => void;
  search?: string;
  withCheckboxes?: boolean;
};

@observer
class CustomerContactPersons extends React.Component<Props> {
  static defaultProps: Partial<Props> = {
    addNewTitle: locale.AddContactPerson(),
    fauxItems: []
  };

  render() {
    const { canAdd, loading } = this.props;

    if (loading) {
      return <Spinner />;
    }

    return (
      <Paper data-test="CustomerContactPersons">
        {this.renderBody()}
        {canAdd && this.renderAddButon()}
      </Paper>
    );
  }

  private renderBody = () => {
    const { data, fauxItems, classes } = this.props;

    const sorted = data.sortBy(
      (x) => !x.item.Roles.some(store().cache.isStaffingRole),
      (x) => x.item.Name
    );

    return (
      <div data-test="CustomerContactPersonsRow" className={classes.root}>
        {fauxItems.map((item) => this.renderFauxItem(item))}
        {sorted.map((item) => this.renderContactPersonRow(item.item, item.selected))}
        {sorted.length === 0 && fauxItems.length === 0 && this.renderEmpty()}
      </div>
    );
  };

  private renderFauxItem = (item: FauxItem) => {
    const { classes } = this.props;
    return (
      <div
        className={classes.fauxRow}
        data-test="CustomerFauxContactPersonRow"
        key={item.title}
        onClick={(e) => {
          e.stopPropagation();
          item.onClick();
        }}
      >
        <ErrorIcon color="error" style={{ width: 48 }} />

        <div className={classes.flexCol}>
          <div className={classes.flexRow}>
            <AccountGroup />
            <Title className={classes.title}>{item.title}</Title>
          </div>
          <div style={{ display: "flex" }}>
            <i>
              <Body style={{ fontStyle: "italic" }}>{item.desc}</Body>
            </i>
          </div>
        </div>

        <IconButton className={classes.addBtn} data-test="AddFauxButton">
          <PersonAddIcon />
        </IconButton>
      </div>
    );
  };

  private renderAddButon = () => {
    const { classes, onAdd } = this.props;
    return (
      <div className={classes.addNewContact}>
        <PrimaryButton onClick={onAdd} data-test="AddBtn" className={classes.addNewBtn}>
          {this.props.addNewTitle}
        </PrimaryButton>
      </div>
    );
  };

  private renderEmpty = () => {
    const { classes } = this.props;
    return (
      <div className={classes.empty}>
        <Body>{locale.Empty}</Body>
      </div>
    );
  };

  private renderContactPersonRow = (row: ContactPerson, selected: boolean) => {
    const { onDelete } = this.props;

    return (
      <CustomerContactCard
        item={row}
        selected={selected}
        {...this.props}
        onClick={() => (this.props.onClick ? this.props.onClick(row) : undefined)}
        onDelete={onDelete ? () => onDelete(row) : undefined}
        key={row._id}
        withCheckbox={this.props.withCheckboxes}
      />
    );
  };
}

const styles = (theme: ThemeType) => ({
  root: {
    "& > div:first-child": {
      borderTopLeftRadius: theme.shape.borderRadius,
      borderTopRightRadius: theme.shape.borderRadius
    }
  },

  fauxRow: {
    backgroundColor: transparentize(0.7, theme.palette.error.light),
    borderBottom: `1px solid ${transparentize(0.85, theme.palette.error.light)}`,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    flexDirection: "row",
    paddingTop: 10,
    paddingBottom: 10,
    "&:hover": {
      backgroundColor: transparentize(0.5, theme.palette.error.light)
    }
  },

  flexRow: {
    display: "flex",
    flexDirection: "row"
  },

  flexCol: {
    display: "flex",
    flexDirection: "column"
  },

  title: {
    fontSize: 15,
    marginLeft: 5,
    marginTop: 2,
    fontWeight: "bold"
  },

  addBtn: {
    position: "absolute",
    // top: 0,
    right: 20
  },

  highlighted: {
    backgroundColor: theme.palette.primary.background
  },

  error: {
    backgroundColor: theme.palette.error.background
  },

  empty: {
    height: 50,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderBottom: "1px solid " + theme.separatorColor
  },

  addNewContact: {
    padding: 15,
    display: "flex",
    alignItems: "center",
    justifyContent: "center"
  },

  addNewBtn: {
    textTransform: "none",
    fontWeight: "bold"
  }
});

export default withStyles(styles as any)(CustomerContactPersons);
